import axios from "axios";
import AuthService from "./AuthService";
import { getAccessToken } from "./TokenService";
import axiosRetry from "axios-retry";
import { RMAXLogger } from "../services/LoggerService/RMAXLogger";
import { isGetUserInfoAndHasReadOnlyRole, checkWritePermissionForUser  } from "./utils";

axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay });

const getFailedAPIResponseToken = (authToken) => {
  const tokenString = authToken?.split(' ');
  return tokenString?.[1];
}

const isResponseTokenExpired = (originalRequest) => {
  /* 
    We are cross-verifying the recently updated token in local storage with the token received in the API response. 
    If the authentication token from the API request has expired and, before that, a different API token had also expired, 
    leading to an update in the local storage/keycloak token, there might be a scenario where one of the tokens in local storage/keycloak is up-to-date while the other coming with api is outdated.
  */
  return getAccessToken() !== getFailedAPIResponseToken(originalRequest?.headers?.Authorization);
}

class Interceptor {
  configure = () => {
    axios.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (!token) {
        // If no access token is found (e.g., logged out in another tab), redirect user to login.
        AuthService.doLogin();
        return Promise.reject(new Error("No access token, redirecting to login."));
      }
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      const apiName = config.apiName;
      delete config.apiName;
      if (this.hasReadOnlyRole) {
        return checkWritePermissionForUser(config, apiName);
      }
      return config;
    });

    axios.interceptors.response.use(
      (response) => {
        if (isGetUserInfoAndHasReadOnlyRole(response)) {
          this.hasReadOnlyRole = true;
        }
        return response
      }, (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401) {
          const localStorageAccessToken= getAccessToken();
          const isAccessTokenExpired = AuthService.hasAccessTokenExpired();
          const hasResponseTokenExpired = isResponseTokenExpired(originalRequest);
          if (isAccessTokenExpired) {
            return AuthService.updateToken().then(() => axios(originalRequest));
          } else if (isResponseTokenExpired) {
            return axios(originalRequest);
          }
          RMAXLogger.error('No retry on 401', {
            action:"401_RetryFailed",
            originalRequest,
            localStorageAccessToken,
            hasAccessTokenExpired: isAccessTokenExpired, //Added this extra log to find Access token expiry issue 
            hasResponseTokenExpired //Added this extra log to find Access token expiry issue 
          });
        }
        if (error.response?.status === 403) {
          return AuthService.handleUserAccess();
        }
        return error;
      }
    );
  };
}

export default new Interceptor();
